/**
 * Field Mapper Utility
 * 
 * แปลงข้อมูลระหว่าง UI Model และ Parse Object
 */

import { FieldMap, ParseVersion } from '../field-mappings/types';

/**
 * Get nested value from object using dot notation
 * e.g., getNestedValue(obj, 'wallet.balance') => obj.wallet.balance
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split('.');
  let value: unknown = obj;
  
  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }
    value = (value as Record<string, unknown>)[key];
  }
  
  return value;
}

/**
 * Set nested value in object using dot notation
 * e.g., setNestedValue(obj, 'wallet.balance', 100) => obj.wallet.balance = 100
 */
function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let current = obj;
  
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current)) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  
  current[keys[keys.length - 1]] = value;
}

/**
 * Map Parse object to UI model
 */
export function mapFromParse<T>(
  parseObject: Record<string, unknown>,
  fieldMap: FieldMap,
  version: ParseVersion
): T {
  const result: Record<string, unknown> = {};
  
  for (const [uiField, mapping] of Object.entries(fieldMap)) {
    const parseField = version === 'v1' ? mapping.parseV1Field : mapping.parseV2Field;
    let value = getNestedValue(parseObject, parseField);
    
    // Apply default value if undefined
    if (value === undefined && mapping.defaultValue !== undefined) {
      value = mapping.defaultValue;
    }
    
    // Apply transform function
    if (mapping.fromParse && value !== undefined) {
      value = mapping.fromParse(value, version);
    }
    
    result[uiField] = value;
  }
  
  return result as T;
}

/**
 * Map UI model to Parse object
 */
export function mapToParse<T extends Record<string, unknown>>(
  uiObject: T,
  fieldMap: FieldMap,
  version: ParseVersion
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  
  for (const [uiField, mapping] of Object.entries(fieldMap)) {
    const value = uiObject[uiField];
    
    // Skip undefined values unless required
    if (value === undefined && !mapping.required) {
      continue;
    }
    
    const parseField = version === 'v1' ? mapping.parseV1Field : mapping.parseV2Field;
    let parsedValue = value;
    
    // Apply transform function
    if (mapping.toParse && parsedValue !== undefined) {
      parsedValue = mapping.toParse(parsedValue, version);
    }
    
    setNestedValue(result, parseField, parsedValue);
  }
  
  return result;
}

/**
 * Map array of Parse objects to UI models
 */
export function mapArrayFromParse<T>(
  parseArray: Record<string, unknown>[],
  fieldMap: FieldMap,
  version: ParseVersion
): T[] {
  return parseArray.map(item => mapFromParse<T>(item, fieldMap, version));
}

/**
 * Generate field mapping documentation
 */
export function generateFieldMapDoc(fieldMap: FieldMap): string {
  const lines: string[] = [
    '| UI Field | Parse V1 | Parse V2 | Required | Default |',
    '|----------|----------|----------|----------|---------|',
  ];
  
  for (const [uiField, mapping] of Object.entries(fieldMap)) {
    lines.push(
      `| ${uiField} | ${mapping.parseV1Field} | ${mapping.parseV2Field} | ${mapping.required ? '✓' : ''} | ${mapping.defaultValue ?? ''} |`
    );
  }
  
  return lines.join('\n');
}

/**
 * Validate Parse object against field map
 */
export function validateParseObject(
  parseObject: Record<string, unknown>,
  fieldMap: FieldMap,
  version: ParseVersion
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  for (const [uiField, mapping] of Object.entries(fieldMap)) {
    if (mapping.required) {
      const parseField = version === 'v1' ? mapping.parseV1Field : mapping.parseV2Field;
      const value = getNestedValue(parseObject, parseField);
      
      if (value === undefined || value === null) {
        errors.push(`Missing required field: ${parseField} (UI: ${uiField})`);
      }
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Compare field values between two versions
 */
export function compareVersionFields(
  parseV1Object: Record<string, unknown>,
  parseV2Object: Record<string, unknown>,
  fieldMap: FieldMap
): { field: string; v1Value: unknown; v2Value: unknown; match: boolean }[] {
  const comparison: { field: string; v1Value: unknown; v2Value: unknown; match: boolean }[] = [];
  
  for (const [uiField, mapping] of Object.entries(fieldMap)) {
    const v1Value = getNestedValue(parseV1Object, mapping.parseV1Field);
    const v2Value = getNestedValue(parseV2Object, mapping.parseV2Field);
    
    comparison.push({
      field: uiField,
      v1Value,
      v2Value,
      match: JSON.stringify(v1Value) === JSON.stringify(v2Value),
    });
  }
  
  return comparison;
}













